/**
 * Voluum-style picker for dynamic URL tokens (lander URL).
 * For Media Devoted | Created By Ciro Autiero
 */

import { type FC, useState } from 'react';
import { ChevronDown, ChevronRight, Info, ExternalLink } from 'lucide-react';

import { Button } from '@/components/ui/layout/Button';
import { cn } from '@/lib/utils';

import {
  appendVoluumTokenToUrl,
  VOLUUM_LANDER_URL_TOKEN_LIST,
  VOLUUM_TRACKING_TOKENS_DOC_URL,
} from './voluum-tracking-tokens';

export interface VoluumUrlTokensPanelProps {
  url: string;
  disabled?: boolean;
  onUrlChange: (nextUrl: string) => void;
}

const VoluumUrlTokensPanel: FC<VoluumUrlTokensPanelProps> = ({
  url,
  disabled,
  onUrlChange,
}) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="rounded-md border border-border/80 bg-card">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-1.5 px-2 py-2 text-left text-xs font-semibold text-primary hover:bg-muted/40"
        aria-expanded={expanded}
      >
        {expanded ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0" aria-hidden />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden />
        )}
        Available URL tokens
      </button>
      {expanded ? (
        <div className="space-y-2 border-t border-border/60 px-2 pb-2 pt-2">
          <div className="flex gap-2 rounded-md border-l-4 border-primary/40 bg-primary/5 px-2 py-1.5 text-[11px] leading-snug text-muted-foreground">
            <Info
              className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary"
              aria-hidden
            />
            <p>
              Adding tokens to your lander URL is optional. Use them for
              dynamic parameters (for example dynamic call-outs on the lander).
              Click a token to append it to the URL.
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {VOLUUM_LANDER_URL_TOKEN_LIST.map((token) => (
              <button
                key={token}
                type="button"
                disabled={disabled}
                title={token}
                onClick={() =>
                  onUrlChange(appendVoluumTokenToUrl(url, token))
                }
                className={cn(
                  'rounded-md border px-2 py-1 font-mono text-[11px] leading-none transition-colors',
                  'hover:bg-primary/10 hover:text-primary',
                  disabled && 'pointer-events-none opacity-50',
                  url.includes(token)
                    ? 'border-primary/50 bg-primary/15 text-foreground'
                    : 'border-border/70 bg-muted/50 text-foreground',
                )}
              >
                {token}
              </button>
            ))}
          </div>
          <Button variant="link" size="sm" className="h-auto p-0 text-xs" asChild>
            <a
              href={VOLUUM_TRACKING_TOKENS_DOC_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1"
            >
              Tokens dictionary
              <ExternalLink className="h-3 w-3" aria-hidden />
            </a>
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export default VoluumUrlTokensPanel;
